import * as React from 'react'
import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'

interface TooltipProps {
  contentRenderer: () => React.ReactNode
}

/**
 * Display whatever {@link TooltipProps#contentRenderer} returns when the children is hovered over.
 *
 * Content is not rendered until hover happens. For example,
 * if you are making a network request inside the contentRenderer,
 * the request won't fire until the user hovers over this component.
 *
 * @example
 * // When you hover over the text "bar", the text "foo" will appear on the top.
 * <Tooltip contentRenderer={() => "foo"}>bar</Tooltip>
 */
const Tooltip = (props: React.PropsWithChildren<TooltipProps>) => {
  const { active, onMouseOver, onMouseOut } = useHover(600)

  let content: React.ReactNode
  if (active) {
    content = props.contentRenderer()
  }

  return (
    <TooltipRoot>
      <div onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
        {props.children}
      </div>
      <TooltipContentRoot>
        <TooltipContentBody show={Boolean(content)}>{content}</TooltipContentBody>
      </TooltipContentRoot>
    </TooltipRoot>
  )
}

/**
 * Custom React hook for managing hover-over.
 * @param delayMs the time it takes to become active/inactive when you hover over/out
 */
function useHover(delayMs: number) {
  let timer: NodeJS.Timer | undefined
  const [active, setActive] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const onMouseOver = () => setIsHovered(true)
  const onMouseOut = () => setIsHovered(false)

  useEffect(() => {
    // always clear the timer when `isHovered` changes
    if (timer) {
      clearTimeout(timer)
    }

    if (isHovered) {
      timer = setTimeout(() => setActive(true), delayMs)
    } else {
      timer = setTimeout(() => setActive(false), delayMs)
    }
  }, [isHovered])

  // clean up timer when unmount
  useEffect(
    () => () => {
      if (timer) {
        clearTimeout(timer)
      }
    },
    []
  )

  return { active, isHovered, onMouseOver, onMouseOut }
}

const TooltipRoot = styled.div`
  display: flex;
`

const TooltipContentRoot = styled.span`
  position: relative;
`

const TooltipContentBody = styled.div`
  ${(props: { show: boolean }) => css`
    opacity: ${props.show ? 1 : 0};
    ${props.show &&
    `
      position: absolute;
      border: solid 1px #2f2b30;
      border-radius: 4px;
      top: -6px;
      left: 8px;
      background: #101011;
      width: max-content;
      transition: opacity 0.2s ease-in;
      user-select: none;
    `}
  `}
`

export default Tooltip
